import React from "react";
import { Box, Typography, Chip, List, ListItem, Divider } from "@mui/material";
import { getSymbolsByCategory } from "../../utils/stitchSymbols";
import "./StitchGuide.css";

const StitchGuide = () => {
  const stitchCategories = getSymbolsByCategory();

  const StitchChip = ({ symbol }) => (
    <Chip label={symbol} className="stitch-chip" />
  );

  const StitchItem = ({ stitch }) => (
    <ListItem className="stitch-item">
      <Box className="stitch-item-content">
        <Box className="stitch-symbol-container">
          <StitchChip symbol={stitch.symbol} />
        </Box>
        <Box className="stitch-details">
          <Typography variant="body1" className="stitch-meaning" fontWeight="medium">
            {stitch.meaning}
            {stitch.abbreviation && (
              <Typography component="span" variant="body2" color="text.secondary" className="ms-2">
                ({stitch.abbreviation})
              </Typography>
            )}
          </Typography>
          <Typography variant="body2" color="text.secondary" className="stitch-description">
            {stitch.description}
          </Typography>
        </Box>
      </Box>
    </ListItem>
  );

  return (
    <Box className="stitch-guide-container">
      <Typography variant="body1" className="mb-3">
        This guide shows all stitch symbols available in Loops & Purls, based on Craft Yarn Council
        international standards.
      </Typography>

      {Object.entries(stitchCategories).map(
        ([categoryName, stitches], categoryIndex) => (
          <Box key={categoryName} className="stitch-category">
            <Typography variant="h6" className="stitch-category-title" color="secondary">
              {categoryName} Stitches
            </Typography>
            <List dense>
              {stitches.map((stitch, index) => (
                <StitchItem key={index} stitch={stitch} />
              ))}
            </List>
            {categoryIndex < Object.keys(stitchCategories).length - 1 && (
              <Divider className="stitch-divider" />
            )}
          </Box>
        ),
      )}

      <Box className="stitch-techniques-section">
        <Divider className="stitch-divider mb-3" />
        <Typography variant="h6" className="section-heading" color="secondary">
          How to Perform These Techniques
        </Typography>
        <Typography variant="body1" className="mb-3">
          Here's a quick guide on how to actually work these stitches:
        </Typography>

        <Typography variant="subtitle1" className="mb-2 mt-3" fontWeight="medium">
          Increases
        </Typography>
        <ul className="feature-list">
          <li>
            <span className="feature-item-title">Yarn Over (YO)</span>
            <span className="feature-item-description">
              Bring yarn over the needle from front to back (or back to front depending on the
              stitch before and after). The exact motion depends on context, but always creates a
              decorative hole and adds one stitch. Common in lace patterns.
            </span>
          </li>
          <li>
            <span className="feature-item-title">Make 1 (M1L/M1R)</span>
            <span className="feature-item-description">
              Pick up the horizontal bar between stitches with left needle from front (M1L) or back
              (M1R), then knit through the appropriate loop to create the slant. Creates an
              invisible increase with no hole.
            </span>
          </li>
          <li>
            <span className="feature-item-title">Knit Front & Back (KFB)</span>
            <span className="feature-item-description">
              Knit into the front loop of the stitch, then without removing it from the needle,
              knit into the back loop. Creates a visible bump.
            </span>
          </li>
        </ul>

        <Typography variant="subtitle1" className="mb-2 mt-3" fontWeight="medium">
          Decreases
        </Typography>
        <ul className="feature-list">
          <li>
            <span className="feature-item-title">Knit 2 Together (K2Tog)</span>
            <span className="feature-item-description">
              Insert right needle through two stitches from left to right, knit them together.
              Slants to the right.
            </span>
          </li>
          <li>
            <span className="feature-item-title">Slip Slip Knit (SSK)</span>
            <span className="feature-item-description">
              Slip one stitch knitwise, slip another stitch knitwise, insert left needle into front
              loops of both slipped stitches, knit them together through back loops. Slants to the
              left, creating a mirror to K2Tog.
            </span>
          </li>
          <li>
            <span className="feature-item-title">Purl 2 Together (P2Tog)</span>
            <span className="feature-item-description">
              Insert right needle through two stitches from right to left, purl them together.
            </span>
          </li>
        </ul>

        <Typography variant="subtitle1" className="mb-2 mt-3" fontWeight="medium">
          Cables
        </Typography>
        <ul className="feature-list">
          <li>
            <span className="feature-item-title">Cable Right (C2R, C3R, etc.)</span>
            <span className="feature-item-description">
              Slip the first specified number of stitches to cable needle, hold cable needle in
              back. Knit the next stitches from left needle, then knit stitches from cable needle.
              Cable crosses to the right.
            </span>
          </li>
          <li>
            <span className="feature-item-title">Cable Left (C2L, C3L, etc.)</span>
            <span className="feature-item-description">
              Slip the first specified number of stitches to cable needle, hold cable needle in
              front. Knit the next stitches from left needle, then knit stitches from cable needle.
              Cable crosses to the left.
            </span>
          </li>
        </ul>

        <Typography variant="subtitle1" className="mb-2 mt-3" fontWeight="medium">
          Lace Patterns
        </Typography>
        <Typography variant="body2" color="text.secondary" className="mb-2">
          Lace patterns combine yarn overs with decreases. The yarn over adds a stitch (creates a
          hole), and the decrease removes a stitch, keeping the stitch count constant while creating
          decorative eyelets.
        </Typography>

        <Typography variant="subtitle1" className="mb-2 mt-3" fontWeight="medium">
          Learning More
        </Typography>
        <Typography variant="body2" color="text.secondary">
          These techniques take practice! Consider making small swatches to practice each technique,
          or search for video tutorials on YouTube for visual demonstrations.
        </Typography>
      </Box>
    </Box>
  );
};

export default StitchGuide;
